// ===============================
// RiskcheckEditor.jsx — OUR STYLE
// ===============================
import React, { useEffect, useMemo, useState, useRef } from "react";
import TableCell from "../../common/ui/TableCell";
import TinyMCEeditor from "../../const/TinyMCEeditor";

export default function RiskcheckEditor({
  initial = {},
  onChange,
  readOnly = false,
  currentUser = {},
}) {
  const editorRef = useRef(null);
  const today = new Date().toISOString().slice(0, 10);

  const safeUser =
    typeof currentUser === "string"
      ? (() => {
          try {
            return JSON.parse(currentUser);
          } catch {
            return {};
          }
        })()
      : currentUser || {};

  // -------------------------------
  // 1) 최상단 문서 정보
  // -------------------------------
  const normalizeTitle = (t) => {
    const v = String(t || "").trim();
    return v && v !== "(제목 없음)" ? v : "";
  };
  const [form, setForm] = useState({
    doc_date: initial.doc_date || today,
    status: initial.status || "none",
    title: normalizeTitle(initial.title),
    note: initial.note || "",
    author: initial.author || safeUser.name || "",
    created_at: initial.created_at || today,
  });

  // -------------------------------
  // 2) 평가 메타 정보
  // -------------------------------
  const [meta, setMeta] = useState({
    process: initial.process || "",
    location: initial.location || "",
    assessor: initial.assessor || "",
    reviewer: initial.reviewer || "",
    approver: initial.approver || "",
    risk_level: initial.risk_level || "",
    severity_score: initial.severity_score || "",
    likelihood_score: initial.likelihood_score || "",
    method: initial.method || "",
  });

  // 위험요소 / 개선계획
  const [hazards, setHazards] = useState(
    Array.isArray(initial.hazards) && initial.hazards.length > 0
      ? initial.hazards
      : [{ hazard: "", consequence: "", existing_measures: "" }]
  );

  const [improvements, setImprovements] = useState(
    Array.isArray(initial.risk_improvements) &&
      initial.risk_improvements.length > 0
      ? initial.risk_improvements
      : [{ risk_element: "", improve_plan: "", owner: "", improve_date: "" }]
  );

  const [showMeta, setShowMeta] = useState(false);
  const [showHazards, setShowHazards] = useState(true);
  const [showImprovements, setShowImprovements] = useState(true);

  const onChangeForm = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const onChangeMeta = (k, v) => setMeta((p) => ({ ...p, [k]: v }));

  const autoTitle = useMemo(() => {
    const proc = meta.process?.trim();
    const loc = meta.location?.trim();
    const d = (form.doc_date || today).toString().slice(0, 10);
    if (proc && loc) return `[위험성 평가] ${proc} - ${loc} (${d})`;
    if (proc) return `[위험성 평가] ${proc} (${d})`;
    if (loc) return `[위험성 평가] ${loc} (${d})`;
    return `[위험성 평가] ${d}`;
  }, [meta, form.doc_date, today]);

  // -------------------------------
  // 부모로 데이터 전달
  // -------------------------------
  useEffect(() => {
    const editorNote =
      editorRef.current?.getContent?.({ format: "html" }) || form.note || "";

    onChange?.({
      dns: initial.dns || "",
      doc_type: "riskcheck",
      title: normalizeTitle(form.title) || autoTitle,
      status: form.status,
      meta: {
        ...meta,
        doc_date: form.doc_date,
        note: editorNote,
        hazards,
        risk_improvements: improvements,
      },
    });
  }, [form, meta, hazards, improvements, autoTitle, onChange]);

  // -------------------------------
  // 위험요소 조작 함수
  // -------------------------------
  const addHazard = () =>
    setHazards((p) => [
      ...p,
      { hazard: "", consequence: "", existing_measures: "" },
    ]);

  const rmHazard = (i) =>
    setHazards((p) => (p.length > 1 ? p.filter((_, idx) => idx !== i) : p));

  const upHazard = (i, k, v) =>
    setHazards((p) => p.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  // -------------------------------
  // 개선계획 조작 함수
  // -------------------------------
  const addImp = () =>
    setImprovements((p) => [
      ...p,
      { risk_element: "", improve_plan: "", owner: "", improve_date: "" },
    ]);

  const rmImp = (i) => setImprovements((p) => p.filter((_, idx) => idx !== i));

  const upImp = (i, k, v) =>
    setImprovements((p) =>
      p.map((r, idx) => (idx === i ? { ...r, [k]: v } : r))
    );

  const section = "";

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="space-y-4 px-2 py-4">
      {/* 문서 기본 정보 (모바일 최적화) */}
      <section className="space-y-3">
        {/* 제목 */}
        <div className="bg-white rounded-lg p-3 border border-gray-200">
          <label className="block text-xs font-medium text-gray-700 mb-1.5">
            제목
          </label>
          {!readOnly ? (
            <div>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={autoTitle}
                value={form.title}
                onChange={(e) => onChangeForm("title", e.target.value)}
              />
              {autoTitle && (
                <p className="mt-1 text-xs text-gray-500">
                  미입력 시 자동 제목: {autoTitle}
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-900">{form.title || autoTitle}</p>
          )}
        </div>

        {/* 평가일 */}
        <div className="bg-white rounded-lg p-3 border border-gray-200">
          <label className="block text-xs font-medium text-gray-700 mb-1.5">
            평가일
          </label>
          {!readOnly ? (
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={form.doc_date}
              onChange={(e) => onChangeForm("doc_date", e.target.value)}
            />
          ) : (
            <p className="text-sm text-gray-900">{form.doc_date}</p>
          )}
        </div>

        {/* 문서 작성일 */}
        <div className="bg-white rounded-lg p-3 border border-gray-200">
          <label className="block text-xs font-medium text-gray-700 mb-1.5">
            문서 작성일
          </label>
          <p className="text-sm text-gray-900">{form.created_at || today}</p>
        </div>

        {/* 비고 */}
        <div className="bg-white rounded-lg p-3 border border-gray-200">
          <label className="block text-xs font-medium text-gray-700 mb-1.5">
            비고
          </label>
          {!readOnly ? (
            <TinyMCEeditor
              initialValue={form.note || ""}
              editorRef={editorRef}
              onChange={(v) => onChangeForm("note", v)}
            />
          ) : (
            <div
              className="text-sm text-gray-900 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: form.note }}
            />
          )}
        </div>
      </section>

      {/* 작업 및 평가 정보 (모바일 최적화) */}
      <section className="bg-white rounded-lg p-3 border border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium text-gray-900">
            {showMeta ? "작업 및 평가 정보" : "평가 세부사항"}
          </div>
          <button
            type="button"
            className="px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200"
            onClick={() => setShowMeta((v) => !v)}
          >
            {showMeta ? "닫기" : "펼치기"}
          </button>
        </div>

        {showMeta && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                공정
              </label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={meta.process}
                onChange={(e) => onChangeMeta("process", e.target.value)}
                disabled={readOnly}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                작업 위치
              </label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={meta.location}
                onChange={(e) => onChangeMeta("location", e.target.value)}
                disabled={readOnly}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                평가자
              </label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={meta.assessor}
                onChange={(e) => onChangeMeta("assessor", e.target.value)}
                disabled={readOnly}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                평가 방법
              </label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="JSA"
                value={meta.method}
                onChange={(e) => onChangeMeta("method", e.target.value)}
                disabled={readOnly}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                위험 수준
              </label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={meta.risk_level}
                onChange={(e) => onChangeMeta("risk_level", e.target.value)}
                disabled={readOnly}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                심각도 / 가능성
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min={1}
                  max={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="심각도"
                  value={meta.severity_score}
                  onChange={(e) =>
                    onChangeMeta("severity_score", e.target.value)
                  }
                  disabled={readOnly}
                />
                <div className="relative flex-1">
                  <input
                    type="number"
                    min={1}
                    max={100}
                    className="w-full px-3 py-2 pr-6 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="가능성"
                    value={meta.likelihood_score}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (v < 1) return onChangeMeta("likelihood_score", 1);
                      if (v > 100) return onChangeMeta("likelihood_score", 100);
                      onChangeMeta("likelihood_score", v);
                    }}
                    disabled={readOnly}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                    %
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* 위험 요소 (모바일 최적화) */}
      <section className="bg-white rounded-lg p-3 border border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium text-gray-900">위험 요소</div>
          <button
            type="button"
            className="px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200"
            onClick={() => setShowHazards((v) => !v)}
          >
            {showHazards ? "닫기" : "펼치기"}
          </button>
        </div>

        {showHazards && (
          <div className="space-y-3">
            {hazards.map((row, idx) => (
              <div
                key={idx}
                className="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-500">
                    항목 #{idx + 1}
                  </span>
                  {!readOnly && hazards.length > 1 && (
                    <button
                      type="button"
                      className="px-2 py-0.5 bg-red-50 text-red-700 rounded text-xs hover:bg-red-100"
                      onClick={() => rmHazard(idx)}
                    >
                      삭제
                    </button>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    위험요소
                  </label>
                  {readOnly ? (
                    <p className="text-sm text-gray-900 break-words">
                      {row.hazard || "-"}
                    </p>
                  ) : (
                    <input
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={row.hazard}
                      onChange={(e) => upHazard(idx, "hazard", e.target.value)}
                      placeholder="위험 요소 입력"
                    />
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    예상결과
                  </label>
                  {readOnly ? (
                    <p className="text-sm text-gray-900 break-words">
                      {row.consequence || "-"}
                    </p>
                  ) : (
                    <input
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={row.consequence}
                      onChange={(e) =>
                        upHazard(idx, "consequence", e.target.value)
                      }
                      placeholder="예상되는 결과"
                    />
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    기존대책
                  </label>
                  {readOnly ? (
                    <p className="text-sm text-gray-900 break-words">
                      {row.existing_measures || "-"}
                    </p>
                  ) : (
                    <input
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={row.existing_measures}
                      onChange={(e) =>
                        upHazard(idx, "existing_measures", e.target.value)
                      }
                      placeholder="현재 적용 중인 대책"
                    />
                  )}
                </div>
              </div>
            ))}

            {!readOnly && (
              <button
                type="button"
                className="w-full px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100"
                onClick={addHazard}
              >
                + 위험요소 추가
              </button>
            )}
          </div>
        )}
      </section>

      {/* 개선 계획 (모바일 최적화) */}
      <section className="bg-white rounded-lg p-3 border border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium text-gray-900">개선 계획</div>
          <button
            type="button"
            className="px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200"
            onClick={() => setShowImprovements((v) => !v)}
          >
            {showImprovements ? "닫기" : "펼치기"}
          </button>
        </div>

        {showImprovements && (
          <div className="space-y-3">
            {improvements.map((row, idx) => (
              <div
                key={idx}
                className="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-500">
                    개선 #{idx + 1}
                  </span>
                  {!readOnly && idx !== 0 && (
                    <button
                      type="button"
                      className="px-2 py-0.5 bg-red-50 text-red-700 rounded text-xs hover:bg-red-100"
                      onClick={() => rmImp(idx)}
                    >
                      삭제
                    </button>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    개선 대상
                  </label>
                  {readOnly ? (
                    <p className="text-sm text-gray-900 break-words">
                      {row.risk_element || "-"}
                    </p>
                  ) : (
                    <input
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={row.risk_element}
                      onChange={(e) =>
                        upImp(idx, "risk_element", e.target.value)
                      }
                      placeholder="개선이 필요한 항목"
                    />
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    개선 계획
                  </label>
                  {readOnly ? (
                    <p className="text-sm text-gray-900 break-words">
                      {row.improve_plan || "-"}
                    </p>
                  ) : (
                    <input
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={row.improve_plan}
                      onChange={(e) =>
                        upImp(idx, "improve_plan", e.target.value)
                      }
                      placeholder="구체적인 개선 방안"
                    />
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      담당
                    </label>
                    {readOnly ? (
                      <p className="text-sm text-gray-900">
                        {row.owner || "-"}
                      </p>
                    ) : (
                      <input
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={row.owner}
                        onChange={(e) => upImp(idx, "owner", e.target.value)}
                        placeholder="담당자"
                      />
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      완료일
                    </label>
                    {readOnly ? (
                      <p className="text-sm text-gray-900">
                        {row.improve_date || "-"}
                      </p>
                    ) : (
                      <input
                        type="date"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={row.improve_date}
                        onChange={(e) =>
                          upImp(idx, "improve_date", e.target.value)
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}

            {!readOnly && (
              <button
                type="button"
                className="w-full px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100"
                onClick={addImp}
              >
                + 개선계획 추가
              </button>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
